/**
 * useDrafting — Draft job creation, polling, and status management.
 *
 * May-2026 routing fix: accepts optional `docType` (mapped to backend
 * DraftJobCreateRequest.doc_type) and `documentText` which is inlined
 * into the prompt body so the existing /api/draft-jobs surface (which
 * does not accept a separate document_text field) can still receive
 * context from an attached file / vault doc when the user invokes
 * drafting with an attachment.
 *
 * May-12 2026 URL hotfix: the POST + GET URLs were missing the `/api`
 * prefix (latent since the hook was authored — never caught because
 * `callDraftAPI` was dead code until the routing fix activated it).
 * Production symptom: `POST https://bayenah.com/draft-jobs` returned
 * the SPA's index.html (200), axios parsed no `draft_id` field, threw
 * "Draft job was not created" with zero observability. Verified via
 * curl: `/api/draft-jobs` → 401 (endpoint exists), `/draft-jobs` → 404.
 *
 * `[DRAFTING_FLOW]` console logs added at every step so future
 * failures surface a timestamped breadcrumb trail in DevTools.
 */
import { useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const API_URL = getBackendBaseUrl();
// Per-call wall-clock cap on the full drafting flow. Sized to match the
// backend's DRAFTING_SYNC_LATENCY_BUDGET_SECONDS (480s) + a 60s safety
// margin so the frontend deadline NEVER expires before the backend's
// own outer wait_for can mark the job failed cleanly. Previous value
// (240s with × 2 doubling) was technically also 480s but opaque; this
// is explicit so future tuning is obvious.
const DRAFTING_TIMEOUT_MS = 540000;
const DRAFTING_POLL_DEADLINE_MS = 540000;

// Cluster C-5 (Feb 06 2026): heuristic lifted into a shared module
// at `src/lib/resolveLanguage.js`. Both useDrafting and any future
// caller delegate to it. Backend rule unchanged.
// iter90 hook lift: `useEffectiveLanguage()` enforces single-call-site
// discipline so the bare UI signal cannot leak into a POST body.
import { containsArabic } from '@/lib/resolveLanguage';
import useEffectiveLanguage from '@/hooks/useEffectiveLanguage';

// Centralised log helper — single grep target.
const logFlow = (step, fields) => {
  // eslint-disable-next-line no-console
  console.log('[DRAFTING_FLOW]', step, fields || {});
};

export const useDrafting = () => {
  const { getAccessToken } = useAuth();
  const { language: uiLanguage } = useLanguage();
  const getEffectiveLanguage = useEffectiveLanguage();

  const callDraftAPI = useCallback(async (
    messages, conversationId, signal, requestOptions = {},
  ) => {
    const timeoutMs = Number(requestOptions?.timeoutMs) || DRAFTING_TIMEOUT_MS;
    const pollIntervalMs = 3000;
    // Explicit poll deadline aligned with backend budget+margin. We
    // honour caller-provided timeouts but never go below the backend
    // budget — that way a transient slow stage doesn't drop the user
    // into "REQUEST_TIMEOUT" while the backend is still finishing.
    const pollMaxDurationMs = Math.max(timeoutMs, DRAFTING_POLL_DEADLINE_MS);
    const docType = requestOptions?.docType || undefined;
    const documentText = (requestOptions?.documentText || '').trim();
    const onStage = typeof requestOptions?.onStage === 'function' ? requestOptions.onStage : null;

    logFlow('callDraftAPI:start', {
      messages_count: (messages || []).length,
      has_doc_type: Boolean(docType),
      has_document_text: documentText.length > 0,
      ui_language: uiLanguage,
    });

    const token = await getAccessToken();
    if (!token) {
      logFlow('callDraftAPI:fail', { step: 'auth', error: 'no_token' });
      throw new Error('Not authenticated');
    }

    const latestUserMessage = [...(messages || [])]
      .reverse()
      .find((msg) => msg?.role === 'user' && (msg?.content || '').trim());

    if (!latestUserMessage) {
      logFlow('callDraftAPI:fail', { step: 'validate', error: 'no_user_message' });
      throw new Error('Message is required');
    }

    // Resolve the effective language NOW from the actual message text.
    // The LanguageContext defaults to 'en' on first visit; without this
    // override an Arabic prompt would POST language='en' and route to
    // English templates. Logging both inputs so future bug reports
    // show which signal won.
    const effectiveLanguage = getEffectiveLanguage(latestUserMessage.content);
    logFlow('callDraftAPI:language_resolved', {
      ui_language: uiLanguage,
      detected_from_text: containsArabic(latestUserMessage.content) ? 'ar' : 'en',
      effective: effectiveLanguage,
    });

    let messageBody = latestUserMessage.content;
    if (documentText) {
      const isAr = effectiveLanguage === 'ar';
      const header = isAr ? 'سياق المستند المرفق' : 'Attached Document Context';
      const note = isAr
        ? 'استخدم النص التالي كمرجع للصياغة:'
        : 'Use the following text as drafting reference:';
      messageBody = `${messageBody}\n\n---\n${header}\n${note}\n\n${documentText}`;
    }

    const postUrl = `${API_URL}/api/draft-jobs`;
    logFlow('callDraftAPI:posting', {
      url: postUrl,
      message_length: messageBody.length,
      has_doc_type: Boolean(docType),
      language: effectiveLanguage,
    });

    let createResponse;
    try {
      createResponse = await axios.post(
        postUrl,
        {
          message: messageBody,
          conversation_id: conversationId,
          doc_type: docType,
          language: effectiveLanguage,
        },
        {
          signal,
          timeout: 15000,
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (postErr) {
      logFlow('callDraftAPI:fail', {
        step: 'post',
        url: postUrl,
        status: postErr?.response?.status,
        message: postErr?.message,
        detail: postErr?.response?.data?.detail,
      });
      throw postErr;
    }

    const draftId = createResponse?.data?.draft_id;
    logFlow('callDraftAPI:posted', {
      status: createResponse?.status,
      draft_id: draftId,
      has_response_data: Boolean(createResponse?.data),
    });
    if (!draftId) {
      logFlow('callDraftAPI:fail', { step: 'post_no_draft_id', response: createResponse?.data });
      throw new Error('Draft job was not created');
    }

    const sleepWithAbort = (ms) => new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (signal) signal.removeEventListener('abort', onAbort);
        resolve();
      }, ms);
      const onAbort = () => {
        clearTimeout(timer);
        if (signal) signal.removeEventListener('abort', onAbort);
        reject(new Error('Aborted'));
      };
      if (signal) {
        if (signal.aborted) { onAbort(); return; }
        signal.addEventListener('abort', onAbort);
      }
    });

    const statusUrl = (id) => `${API_URL}/api/draft-jobs/${id}`;
    const startedAt = Date.now();
    let attempt = 0;
    while (Date.now() - startedAt < pollMaxDurationMs) {
      if (signal?.aborted) {
        logFlow('callDraftAPI:fail', { step: 'poll_aborted', draft_id: draftId });
        throw new Error('Aborted');
      }
      await sleepWithAbort(pollIntervalMs);
      attempt += 1;

      let statusResponse;
      try {
        statusResponse = await axios.get(statusUrl(draftId), {
          signal,
          timeout: 15000,
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (getErr) {
        logFlow('callDraftAPI:fail', {
          step: 'poll_get',
          draft_id: draftId,
          attempt,
          status: getErr?.response?.status,
          message: getErr?.message,
        });
        throw getErr;
      }

      const payload = statusResponse?.data || {};
      const status = payload?.status;
      logFlow('callDraftAPI:polling', {
        draft_id: draftId,
        attempt,
        status,
        stage: payload?.stage,
        stage_index: payload?.stage_progress?.stage_index,
        elapsed_ms: Date.now() - startedAt,
      });
      // Forward both the legacy `stage` string AND the new
      // structured `stage_progress` payload to the caller. The
      // progress component reads stage_progress; the legacy stage
      // is kept as a fallback for older code paths.
      if (onStage) onStage(payload?.stage || null, payload);

      if (status === 'done') {
        logFlow('callDraftAPI:done', {
          draft_id: draftId,
          word_count: Number(payload?.artifact?.word_count || 0),
          attempts: attempt,
          elapsed_ms: Date.now() - startedAt,
        });
        return {
          text: payload?.draft_text || '',
          conversationId: payload?.conversation_id || conversationId,
          meta: {
            artifact_type: 'draft_document',
            artifact_title: payload?.artifact?.title || (effectiveLanguage === 'ar' ? 'مسودة قانونية' : 'Legal Draft'),
            contract_type: payload?.contract_type || payload?.artifact?.contract_type || 'nda',
            word_count: Number(payload?.artifact?.word_count || 0),
            draft_id: payload?.draft_id || draftId,
            job_stage: payload?.stage || null,
            // Annotations Phase 2: server-emitted clause map used by
            // ClauseTaggedMarkdown to wrap each clause with a
            // data-clause-id container. No frontend regex.
            clause_boundaries: Array.isArray(payload?.clause_boundaries)
              ? payload.clause_boundaries
              : [],
          },
        };
      }

      if (status === 'failed') {
        logFlow('callDraftAPI:fail', {
          step: 'pipeline_failed',
          draft_id: draftId,
          message: payload?.message,
          error_code: payload?.error_code,
        });
        // Throw a structured error so the caller can branch on the
        // backend's machine-readable error_code (e.g., 'stage1_empty')
        // and render an appropriate user-facing bubble. The Error
        // message itself is kept English/Arabic-neutral — the
        // bilingual user-facing text is owned by the frontend i18n
        // layer in AgentPreviewPage (audit-2026-05-12 Bug #1 fix).
        const failure = new Error(payload?.message || 'DRAFT_FAILED');
        failure.error_code = payload?.error_code || null;
        failure.draft_id = draftId;
        failure.is_drafting_failure = true;
        throw failure;
      }
    }
    logFlow('callDraftAPI:fail', {
      step: 'poll_timeout',
      draft_id: draftId,
      attempts: attempt,
      deadline_ms: pollMaxDurationMs,
    });
    throw new Error('REQUEST_TIMEOUT');
  }, [getAccessToken, uiLanguage]);

  return { callDraftAPI, DRAFTING_TIMEOUT_MS };
};
